"use client";

import { ArrowRight, ChartBar as BarChart3, ShieldCheck, Target, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Revenuegovernance() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "OTA Revenue Management",
    description: "We optimize your presence on major OTAs through data-backed listing strategies, dynamic rate adjustments, and high-conversion content management to capture maximum market share.",
    Icon: BarChart3
  }, {
    number: "02",
    title: "Tech Stack Optimization",
    description: "We audit and recommend the ideal hospitality tech stack—from PMS to Channel Managers—ensuring seamless integration and automated workflows that drive operational efficiency.",
    Icon: Zap
  }, {
    number: "03",
    title: "Revenue Audits",
    description: "Our comprehensive revenue audits identify hidden leaks in your booking funnel, providing actionable insights to improve your RevPAR and overall property profitability.",
    Icon: ShieldCheck
  }, {
    number: "04",
    title: "Direct Booking Growth",
    description: "We implement targeted digital strategies to shift your booking mix toward direct channels, reducing commission costs and fostering long-term guest loyalty.",
    Icon: Target
  }];
  return <section data-section-id="5265" id="revenuegovernance" className="bg-slate-50 text-slate-900 relative overflow-hidden py-24 md:py-32 lg:py-40" style={{
    backgroundImage: "radial-gradient(circle, hsl(222 47% 7% / 0.04) 1px, transparent 1px)",
    backgroundSize: "24px 24px"
  }}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={titleRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-default text-xs tracking-[0.2em] text-blue-600 uppercase mb-8">
                [ 06 / EXPERTISE ]
              </p>
              <h2 className="font-default font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-[80px] leading-[0.95] tracking-tight text-slate-900 mb-8">
                Revenue Governance
              </h2>
              <p className="text-base lg:text-xl text-slate-700 leading-relaxed max-w-md mb-10">
                Four pillars of revenue management designed to maximize your hotel's profitability. We combine deep industry expertise with modern data-driven strategies to ensure your property outperforms the competition.
              </p>
              <Link to="/OurServicesPage" className="inline-flex items-center gap-3 font-default text-xs tracking-[0.2em] uppercase text-blue-600 hover:text-blue-800 transition-colors group">
                <span className="border-b border-blue-600/40 group-hover:border-blue-800 pb-1">
                  View All Services
                </span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="flex flex-col">
              {features.map((feature, i) => {
              const {
                ref,
                isVisible
              } = useRevealOnScroll<HTMLDivElement>({
                threshold: 0.25
              });
              const Icon = feature.Icon;
              return <div key={i} data-index={i} ref={ref} className={`group relative py-10 md:py-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: `${i * 80}ms`
              }}>
                    {}
                    <div className={`absolute top-0 left-0 h-px bg-blue-600/70 transition-all duration-1000 ease-out group-hover:bg-blue-600 group-hover:shadow-[0_0_12px_hsl(221_83%_53%/0.6)] ${isVisible ? "w-full" : "w-0"}`} style={{
                  transitionDelay: `${i * 80 + 100}ms`
                }} />

                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-6 mb-5">
                          <span className="font-default text-4xl md:text-5xl lg:text-[64px] font-bold text-slate-300 leading-none tracking-tight transition-transform duration-500 group-hover:-translate-y-1 group-hover:text-blue-600">
                            {feature.number}
                          </span>
                          <h3 className="font-default font-semibold text-2xl md:text-3xl lg:text-[36px] leading-tight tracking-tight text-slate-900">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-xl pl-0 md:pl-[88px] lg:pl-[104px]">
                          {feature.description}
                        </p>
                      </div>

                      <div className="flex-shrink-0 pt-2">
                        <div className="h-12 w-12 md:h-14 md:w-14 rounded-lg border border-slate-200 flex items-center justify-center transition-all duration-500 group-hover:rotate-[8deg] group-hover:border-blue-600/60">
                          <Icon className="h-5 w-5 md:h-6 md:w-6 text-slate-500 group-hover:text-blue-600 transition-colors duration-500" strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>
                  </div>;
            })}

              {}
              <div className="h-px bg-slate-200 w-full" />
            </div>

            {}
            <div className="mt-12 lg:hidden">
              <Button asChild variant="outline" className="font-default text-xs tracking-[0.2em] uppercase border-blue-600/40 text-blue-600 hover:bg-blue-50 hover:text-blue-800">
                <Link to="/OurServicesPage">
                  View All Services
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}